import { toSnake } from '../../utils/wireKeys'

// Pure helpers for the assistant's car decisions (spec: 0040_assistant_car_decisions, w-admin).
// The API sends kinds and outcomes as codes; these turn them into the team's words. An unknown code
// is shown as itself rather than hidden, so a new kind on the API is visible before it is named here.

const QUESTIONS = {
  which_car: 'Confirm which car',
  car_hint: 'Name the car',
  already_done: 'Already checked — chose what next',
  resume: 'Carry on with parked work?',
  // Questions other parts of the assistant ask, recorded as decisions since w-api Phase 3.
  what_next: 'What would you like to do?',
  lookup_choice: 'Pick one of their records'
}

const OUTCOMES = {
  resolved: {
    read_out: 'Read out the earlier estimate',
    started_duty: 'Started a duty check',
    started_history: 'Started a history report',
    resent: 'Sent the report again',
    on_its_way: 'Told them the report is on its way',
    start_new: 'Started fresh for a new car',
    carry_on: 'Carried on where they were',
    cancelled: 'Cancelled',
    resumed: 'Went back to their parked work',
    declined: 'Didn’t want the parked work',
    switched: 'Moved to another service',
    answered: 'Answered'
  },
  lapsed: {
    misses: 'Didn’t answer twice — carried on',
    expired: 'Expired after 10 minutes',
    stopped: 'Customer said stop',
    error: 'Something went wrong — carried on',
    superseded: 'Another question took over'
  }
}

export const decisionQuestion = kind => QUESTIONS[kind] || kind || '—'

export const decisionOutcome = (event, detail) => {
  if (event === 'opened') return 'Question asked'
  const words = OUTCOMES[event] || {}
  return words[detail] || detail || '—'
}

export const DECISION_EVENT_COLORS = {
  opened: 'blue',
  resolved: 'green',
  lapsed: 'default'
}

export const decisionEventColor = event => DECISION_EVENT_COLORS[event] || 'default'

// "expires in 7 min" / "expires in under a minute" / "expired". `now` is injectable for tests.
export const expiresIn = (expiresAt, now = Date.now()) => {
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) return ''
  const seconds = Math.round((at - now) / 1000)
  if (seconds <= 0) return 'expired'
  if (seconds < 60) return 'expires in under a minute'
  return `expires in ${Math.round(seconds / 60)} min`
}

// The pilot report's two tables: one row per outcome or lapse reason, with its share of the
// decisions opened. Empty input gives no rows, which the page shows as its empty state.
export const carDecisionRows = (block, group) => {
  const counts = (block && block[group]) || {}
  const opened = Number(block?.opened) || 0
  // The API camelizes map keys (`read_out` arrives as `readOut`), so each is read back the
  // server's way before it is named.
  return Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map(([raw, count]) => [toSnake(raw), count])
    .map(([key, count]) => ({
      key,
      label: decisionOutcome(group, key),
      count: Number(count),
      opened
    }))
}

// "Parked — duty check for the 2018 Corolla" for a conversation whose work is waiting
// (spec Req 7, Phase 2).
export const parkedLine = parked => {
  if (!parked) return ''
  const what = parked.label || 'unfinished work'
  return parked.expired ? `Parked work expired — ${what}` : `Parked — ${what}`
}
